<template>
  <div class="row sba-open-frame">
    <div class="col s12 sba-open-frame-col">
      <LoadingDiv v-if="!isReady" />

      <div class="finance-invoices sba-open-workbench" v-else>
        <div class="invoice-review-head sba-open-head">
          <div class="sba-open-title">
            <label class="active teal-text">Finance Invoices</label>
            <h5>Invoice Approval</h5>
            <p>Search, inspect, and approve premium invoices that have moved into finance review.</p>
          </div>

          <div class="invoice-head-filter" v-show="showSearch">
            <FilterInvoice compact-client-search @search="handleSearch" />
          </div>

          <span class="invoice-count sba-open-count" v-show="!showSearch">
            {{ selectedInvoiceCount }}
            <small>{{ selectedInvoiceCount === 1 ? "invoice" : "invoices" }}</small>
          </span>
        </div>

        <div class="finance-invoices-progress progress" v-if="isPageLoading">
          <div class="indeterminate"></div>
        </div>

        <!-- Invoices -->
        <div class="invoice-list-card">
          <!-- Start -->
          <template v-for="(invoice, index) in invoices" :key="invoice.id">
          <div v-if="currInvoice?.id === invoice?.id || !currInvoice">
            <!-- Invoices -->
            <div
              class="invoice-row-card sba-open-row"
              :class="{
                'is-pending': invoice.status == 'p',
                'is-complete': invoice.status == 'c',
                'is-grace': invoice.status == 'z',
                'is-deleted': invoice.status == 'd',
              }"
            >
              <div class="row mb-zero">
                <div class="col s1">
                  <span class="invoice-number">{{ numberBadge(index) }}</span>
                </div>
                <div class="col s10">
                  <p class="invoice-status-line">
                    <span
                      v-if="
                        invoice.status == 'p' && invoice.start < currServerDate
                      "
                      >Delayed Invoice<i class="tiny material-icons"
                        >schedule</i
                      ></span
                    >
                    <span
                      v-if="
                        invoice.status == 'p' && invoice.start >= currServerDate
                      "
                      >Pending Invoice</span
                    >
                    <span
                      class="orange-text text-darken-4"
                      v-if="invoice.status == 'z'"
                      >Unpaid (Grace Period)</span
                    >
                    <span v-if="invoice.status == 'c'"
                      >Completed Payment<i class="tiny material-icons teal-text"
                        >check_circle</i
                      ></span
                    >
                    <span class="red-text" v-if="invoice.status == 'd'"
                      >Deleted Invoice</span
                    >
                  </p>
                  <h6 class="invoice-client">
                    {{ invoiceClientName(invoice) }}
                    <span class="invoice-category">{{
                      invoiceClientCat(invoice)
                    }}</span>
                  </h6>
                  <h6 class="invoice-amount">
                    ₦{{
                      changeToNaira(invoice.input_total, invoice.output_total)
                    }}
                    <span class="invoice-period">
                      <small>
                        {{
                          new Date(invoice.start).toLocaleString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        }}
                      </small>

                      -

                      <small>
                        {{
                          new Date(invoice.end).toLocaleString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        }}
                      </small>
                    </span>
                  </h6>
                </div>
                <div class="col s1">
                  <span class="right" v-if="!currInvoice">
                    <button
                      type="button"
                      class="invoice-action sba-open-action right"
                      :class="{
                        'is-late': invoice.start < currServerDate && (invoice.status == 'p' || invoice.status == 'z'),
                        'is-normal': !(
                          invoice.start < currServerDate && (invoice.status == 'p' || invoice.status == 'z')
                        ),
                      }"
                      @click="handleView(invoice)"
                      title="view details"
                    >
                      <i class="material-icons">chevron_right</i>
                    </button>
                  </span>
                  <span class="right" v-else>
                    <button type="button" class="invoice-action sba-open-action is-close right" @click="handleClear()" title="go back">
                      <i class="material-icons">keyboard_return</i>
                    </button>
                  </span>
                </div>
              </div>
            </div>

            <div
              class="finance-approval-card"
              v-if="
                currInvoice?.id === invoice?.id &&
                (invoice.status == 'p' || invoice.status == 'z')
              "
            >
              <button
                class="waves-effect waves-light btn-large black"
                :class="{ disabled: isSubmitting }"
                v-if="invoice.status == 'p'"
                @click.prevent="handleSubmit('later')"
              >
                <i class="medium material-icons yellow-text left">schedule</i>
                Pay Later
              </button>
              <button
                class="waves-effect waves-light btn-large teal"
                :class="{ disabled: isSubmitting }"
                @click.prevent="handleSubmit('paid')"
              >
                <i class="medium material-icons right">check_circle</i>Approve
                As Paid
              </button>
            </div>
          </div>
          </template>
        </div>

        <!-- Invoice details -->
        <InvoiceDiv :currInvoice="currInvoice" scope="billable" v-if="currInvoice" />

        <!-- Pages -->
        <template v-else>
          <div class="invoice-empty-state" v-if="!invoices.length">
            <div class="invoice-empty-icon">
              <i class="material-icons">receipt_long</i>
            </div>
            <h6>{{ emptyInvoiceTitle }}</h6>
            <p>{{ emptyInvoiceDescription }}</p>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else />
        </template>
      </div>
    </div>
  </div>
</template>
          
<script>
import { computed, onMounted, ref, watch } from "vue";
import { changeToMoney } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import FilterInvoice from "@/components/invoice/FilterInvoiceDiv.vue";
import InvoiceDiv from "@/components/invoice/InvoiceDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import M from "materialize-css";


export default {
  name: "InvoicesFinanceComponent",
  components: { LoadingDiv, PaginationDiv, InvoiceDiv, FilterInvoice },
  props: ["resetKey"],
  setup(props) {
    const response = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isPageLoading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const currInvoice = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);
    const currServerDate = ref(null);
    const showSearch = ref(true);
    const searchItem = ref(null);
    const searchTitle = ref(null);
    const filterState = ref({
      category: "",
      categoryLabel: "Open Invoices",
      client: "",
      clientLabel: "",
    });

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          currServerDate.value = res.value.server;
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
        // end
        isLoading.value = false;
        isPageLoading.value = false;
      })();
    };

    // to naira
    const changeToNaira = (i, o) => {
      // sort value
      let val = i - o;
      // conclude
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    const invoiceClient = (invoice) => {
      return invoice?.policy?.client || invoice?.client || {};
    };

    const invoiceClientName = (invoice) => {
      return invoiceClient(invoice)?.name || "Client";
    };

    const invoiceClientCat = (invoice) => {
      return invoiceClient(invoice)?.cat?.toUpperCase() || "";
    };

    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // starter
    onMounted(() => {
      // loader
      isReady.value = false;
      // start from the top
      getInvoices();
    });

    // invoices
    const getInvoices = () => {
      // set current Url
      currUrl.value = "group/invoices/group";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value);
      // clear
      handleClear();
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      // start loading
      isLoading.value = true;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set search title
      searchTitle.value = val.searchTitle;
      // set current filter state
      filterState.value = val.filterState || filterState.value;
      // get items from function
      getItemsFromApi(currUrl.value);
      // clear
      handleClear();
    };

    // switch page
    const handlePage = (page) => {
      // loader
      isLoading.value = true;
      isPageLoading.value = true;
      scrollToPageTop();
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
      // clear
      handleClear();
    };

    const scrollToPageTop = () => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    };

    // enrollee ction
    const handleClear = () => {
      currInvoice.value = null;
      showSearch.value = true;
    };

    watch(
      () => props.resetKey,
      () => {
        handleClear();
      }
    );

    // enrollee ction
    const handleView = (val) => {
      currInvoice.value = val;
      showSearch.value = false;
      scrollToPageTop();
    };

    const handleSubmit = async (val) => {
      // start
      isSubmitting.value = true;

      let newStatus = null;

      if (val == "paid") {
        newStatus = "c";
      } else if (val == "later") {
        newStatus = "z";
      }

      if (newStatus) {
        let newVal = {
          group_id: currInvoice.value.id,
          new_status: newStatus,
        };

        // post
        const { res, err } = await postItem(
          "operator/finance/premium/payment/confirmation",
          newVal
        );

        if (res.value) {
          // notify (Toast)
          M.toast({
            html: "Invoice updated <i class='material-icons'>check_circle</i>", // Toast content
            classes: 'green', // Add custom classes // green rounded
            displayLength: 5000, // Duration in milliseconds
            inDuration: 300, // Transition in duration
            outDuration: 300, // Transition out duration
          });
          // start again
          getInvoices();
        } else {
          error.value = err.value;
        }

        // clear
        isSubmitting.value = false;
        // clear
        handleClear();
      }
    };

    // output results
    const invoices = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    const selectedInvoiceCount = computed(() => {
      return Number(currInvoice.value?.billable_invoices_count || currInvoice.value?.invoices_count || 0);
    });

    const emptyInvoiceTitle = computed(() => {
      const categoryLabel = filterState.value.categoryLabel || "Open Invoices";
      const clientLabel = filterState.value.clientLabel;

      if (filterState.value.category === "all" && !clientLabel) {
        return "No invoices found";
      }

      if (clientLabel) {
        return `No invoices found for ${clientLabel}`;
      }

      return `No ${categoryLabel.toLowerCase()} found`;
    });

    const emptyInvoiceDescription = computed(() => {
      const categoryLabel = filterState.value.categoryLabel || "Open Invoices";
      const clientLabel = filterState.value.clientLabel;

      if (filterState.value.category === "all" && !clientLabel) {
        return "There are no invoice records matching the current finance view.";
      }

      if (clientLabel && filterState.value.category !== "all") {
        return `There are no ${categoryLabel.toLowerCase()} records for this client.`;
      }

      if (clientLabel) {
        return "There are no invoice records for this client.";
      }

      return `There are no invoice records under the ${categoryLabel.toLowerCase()} filter.`;
    });

    return {
      searchTitle,
      handleSearch,
      handleSubmit,
      handlePage,
      handleClear,
      handleView,
      numberBadge,
      changeToNaira,
      invoiceClientName,
      invoiceClientCat,
      currServerDate,
      currInvoice,
      showSearch,
      searchItem,
      isReady,
      isLoading,
      isPageLoading,
      isSubmitting,
      error,
      invoices,
      allPages,
      selectedInvoiceCount,
      emptyInvoiceTitle,
      emptyInvoiceDescription,
    };
  },
};
</script>
          
<style scoped>
.progress {
  margin: 0;
}

.finance-invoices-progress {
  border-radius: 0;
  height: 3px;
  margin: 0 0 10px;
}

.finance-invoices {
  color: #263238;
  width: 100%;
}

.invoice-review-head,
.invoice-list-card,
.invoice-row-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
}

.invoice-review-head {
  align-items: flex-end;
  border-color: #f0f0f0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: none;
  display: flex;
  gap: 22px;
  justify-content: space-between;
  margin-bottom: 0;
  padding: 24px 22px;
}

.invoice-review-head h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.invoice-review-head p {
  color: #607d8b;
  line-height: 1.55;
  margin: 8px 0 0;
  max-width: 760px;
}

.invoice-count {
  align-items: flex-end;
  background: #fff8e1;
  border: 1px solid #ffecb3;
  border-radius: 6px;
  color: #ef6c00;
  display: inline-flex;
  flex-direction: column;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.1;
  min-width: 94px;
  padding: 11px 13px;
}

.invoice-count small {
  color: #455a64;
  font-size: 0.75rem;
  font-weight: 600;
}

.invoice-head-filter {
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  box-shadow: none;
  flex: 0 1 650px;
  padding: 6px;
}

.invoice-head-filter :deep(.section) {
  margin: 0;
  padding: 0;
}

.invoice-head-filter :deep(.row) {
  align-items: stretch;
  display: flex;
  gap: 0;
  margin-bottom: 0;
}

.invoice-head-filter :deep(.col) {
  min-width: 0;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.invoice-head-filter :deep(.col + .col) {
  border-left: 1px solid #cfd8dc;
}

.invoice-head-filter :deep(.input-field) {
  height: 48px;
  margin-bottom: 0;
  margin-top: 0;
}

.invoice-head-filter :deep(nav) {
  background: transparent !important;
  border: 0;
  box-shadow: none !important;
  height: 48px;
  line-height: 48px;
}

.invoice-head-filter :deep(.nav-wrapper) {
  background: transparent;
  height: 48px;
  line-height: 48px;
}

.invoice-head-filter :deep(input),
.invoice-head-filter :deep(.select-wrapper input.select-dropdown) {
  background: transparent;
  border: none !important;
  border-radius: 0;
  box-sizing: border-box;
  color: #263238 !important;
  font-size: 1.08rem;
  font-weight: 700;
  height: 48px !important;
  line-height: 48px;
  margin: 0;
  padding: 0 46px 0 18px !important;
  width: 100%;
}

.invoice-head-filter :deep(input:focus) {
  box-shadow: inset 0 -2px 0 #00897b !important;
}

.invoice-head-filter :deep(.material-icons) {
  color: #00897b !important;
  height: 48px;
  line-height: 48px;
  right: 10px;
  top: 0;
}

.invoice-head-filter :deep(label) {
  color: #607d8b !important;
}

.invoice-list-card {
  background: transparent;
  border: none;
  display: grid;
  gap: 3px;
  margin-top: 0;
}

.invoice-row-card {
  align-items: stretch;
  border-left-color: #eeeeee;
  border-left-width: 1px;
  box-shadow: 0 10px 22px -20px rgba(38, 50, 56, 0.55);
  min-height: 116px;
  padding: 24px 20px 24px 24px;
  position: relative;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.invoice-row-card > .row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
}

.invoice-row-card:hover {
  border-color: #d8e1e5;
  box-shadow: 0 14px 28px -22px rgba(38, 50, 56, 0.7);
  transform: translateY(-1px);
}

.invoice-list-card > div:first-child .invoice-row-card {
  border-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: 0 12px 22px -24px rgba(38, 50, 56, 0.68);
}

.invoice-number {
  color: #607d8b;
  font-size: 0.92rem;
  font-weight: 700;
}

.invoice-status-line {
  align-items: center;
  background: #fafafa;
  border-radius: 16px;
  color: #607d8b;
  display: flex;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 5px;
  justify-self: start;
  margin: 0;
  padding: 5px 10px;
  width: max-content;
}

.invoice-status-line i {
  font-size: 18px;
}

.invoice-row-card.is-pending .invoice-status-line {
  background: #ffebee;
  color: #b71c1c;
}

.invoice-row-card.is-grace .invoice-status-line {
  background: #e3f2fd;
  color: #0d47a1;
}

.invoice-row-card.is-complete .invoice-status-line,
.invoice-row-card.is-deleted .invoice-status-line {
  background: #eceff1;
  color: #37474f;
}

.invoice-client {
  align-items: center;
  color: #263238;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.98rem;
  font-weight: 700;
  gap: 8px;
  margin: 0;
}

.invoice-category {
  background: #f5f5f5;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #607d8b;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 3px 7px;
}

.invoice-amount {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #263238;
  display: inline-flex;
  flex-wrap: wrap;
  font-size: 1.08rem;
  font-weight: 800;
  margin: 0;
  padding: 10px 14px;
}

.invoice-period {
  align-items: center;
  background: #eef3f6;
  border-radius: 6px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.92rem;
  font-weight: 700;
  gap: 5px;
  margin-left: 12px;
  padding: 4px 8px;
}

.invoice-row-card .col.s10 {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(170px, 0.9fr) minmax(280px, 1.45fr) minmax(300px, 1.15fr);
}

.invoice-action {
  align-items: center;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 50%;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  padding: 0;
  width: 38px;
}

.invoice-action.is-normal {
  background: #fafafa;
}

.invoice-action.is-late {
  background: #ffebee;
  color: #c62828;
}

.invoice-action.is-close {
  background: #eceff1;
  color: #263238;
}

.invoice-action i {
  font-size: 21px;
}

.invoice-row-card:hover .invoice-action {
  background: #eceff1;
  color: #263238;
}

.finance-approval-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-top: 0;
  box-shadow: 0 12px 22px -24px rgba(38, 50, 56, 0.68);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-bottom: 0;
  padding: 16px 18px;
}

.invoice-empty-state {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-top: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  color: #607d8b;
  padding: 40px 18px;
  text-align: center;
}

.invoice-empty-icon {
  align-items: center;
  background: #f5f7f8;
  border: 1px solid #e5ecef;
  border-radius: 50%;
  display: inline-flex;
  height: 54px;
  justify-content: center;
  margin-bottom: 12px;
  width: 54px;
}

.invoice-empty-icon i {
  color: #90a4ae;
}

.invoice-empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 0 0 6px;
}

.invoice-empty-state p {
  margin: 0 auto;
  max-width: 440px;
}

.spacer10 {
  margin-top: 10px;
  margin-bottom: 10px;
}

.pointer {
  cursor: default;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

/* open */
.chip-more {
  cursor: pointer;
  background-color: #eaecee !important;
}

.chip-more:hover {
  color: white !important;
  background-color: black !important;
}

/* close */
.chip-less {
  cursor: pointer;
  color: white !important;
  background-color: black !important;
}

.chip-less:hover {
  color: black !important;
  background-color: #eaecee !important;
}

/* base class */
.tab-custom {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  font-weight: 600;
  text-align: center;
  border: 1px solid transparent;
}

.tab-custom:hover {
  color: #000000 !important;
}

.tab-custom-active {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  color: white;
  font-weight: 600;
  text-align: center;
  background-color: black;
}

/* display */
.tab-custom-section {
  padding: 20px 0 0;
  border-top: 1px solid #ddd;
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .invoice-review-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;
    padding: 22px;
  }

  .invoice-head-filter {
    flex-basis: auto;
    width: 100%;
  }

  .invoice-head-filter :deep(.row) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .invoice-head-filter :deep(.col) {
    width: 100% !important;
  }

  .invoice-head-filter :deep(.col + .col) {
    border-left: 1px solid #cfd8dc;
    border-top: 0;
  }

  .invoice-count {
    align-items: flex-start;
  }

  .invoice-row-card {
    min-height: 0;
    padding: 22px;
  }

  .invoice-row-card > .row {
    align-items: stretch;
    display: grid;
    gap: 12px 16px;
    grid-template-columns: 40px minmax(0, 1fr) 44px;
  }

  .invoice-row-card .col {
    margin: 0;
    padding: 0;
    width: 100% !important;
  }

  .invoice-row-card .col.s10 {
    grid-column: 1 / -1;
    grid-row: 2;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }

  .invoice-row-card .col.s1:first-child {
    align-self: center;
    grid-column: 1;
    grid-row: 1;
    justify-self: start;
  }

  .invoice-row-card .col.s1:last-child {
    align-self: center;
    grid-column: 3;
    grid-row: 1;
    justify-self: end;
  }

  .invoice-status-line {
    width: fit-content;
  }

  .invoice-client {
    grid-column: 1;
    line-height: 1.35;
  }

  .invoice-amount {
    align-self: stretch;
    background: #f8fafb;
    border-color: #edf1f3;
    box-sizing: border-box;
    display: grid;
    gap: 7px;
    grid-column: 1;
    justify-content: flex-start;
    line-height: 1.25;
    padding: 11px 12px;
    width: 100%;
  }

  .invoice-amount::before {
    color: #78909c;
    content: "Invoice amount";
    display: block;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    line-height: 1;
    text-transform: uppercase;
  }

  .invoice-period {
    background: #263238;
    border: 1px solid #263238;
    color: #ffffff;
    margin-left: 0;
    margin-top: 0;
    width: fit-content;
  }

  .invoice-action {
    border-radius: 6px;
    height: 40px;
    width: 40px;
  }

  .finance-approval-card {
    padding: 16px;
  }
}

@media only screen and (max-width: 600px) {
  .invoice-review-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
  }

  .invoice-review-head h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .finance-invoices {
    width: 100%;
  }

  .invoice-head-filter {
    flex-basis: auto;
    width: 100%;
  }

  .invoice-head-filter :deep(.row) {
    display: grid;
    grid-template-columns: 1fr;
  }

  .invoice-head-filter :deep(.col) {
    width: 100% !important;
  }

  .invoice-head-filter :deep(.col + .col) {
    border-left: 0;
    border-top: 1px solid #cfd8dc;
  }

  .invoice-head-filter :deep(input),
  .invoice-head-filter :deep(.select-wrapper input.select-dropdown) {
    font-size: 1rem;
    padding-left: 14px !important;
  }

  .invoice-count {
    align-items: flex-start;
  }

  .invoice-row-card {
    min-height: 0;
    padding: 20px;
  }

  .invoice-row-card > .row {
    align-items: stretch;
    display: grid;
    gap: 14px;
    grid-template-columns: minmax(0, 1fr) 38px;
  }

  .invoice-row-card .col {
    margin: 0;
    padding: 0;
    width: 100% !important;
  }

  .invoice-row-card .col.s10 {
    grid-column: 1 / -1;
    grid-row: 2;
    grid-template-columns: 1fr;
    gap: 12px;
    min-width: 0;
  }

  .invoice-row-card .col.s1:first-child {
    align-self: start;
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
  }

  .invoice-row-card .col.s1:last-child {
    grid-column: 1 / -1;
  }

  .invoice-row-card .col.s1:last-child > .right {
    display: block;
    float: none !important;
    width: 100%;
  }

  .invoice-number {
    align-items: center;
    background: #f5f8f8;
    border: 1px solid #e0e7ea;
    border-radius: 6px;
    display: inline-flex;
    height: 38px;
    justify-content: center;
    width: 38px;
  }

  .invoice-status-line {
    box-sizing: border-box;
    justify-content: flex-start;
    line-height: 1.3;
    max-width: 100%;
    padding: 7px 10px;
    width: fit-content;
  }

  .invoice-client {
    align-items: flex-start;
    flex-direction: column;
    line-height: 1.35;
    margin-top: 1px;
    min-width: 0;
  }

  .invoice-category {
    width: fit-content;
  }

  .invoice-amount {
    background: #f8fafb;
    border-color: #edf1f3;
    box-sizing: border-box;
    display: grid;
    gap: 7px;
    line-height: 1.25;
    padding: 12px;
    width: 100%;
  }

  .invoice-amount::before {
    color: #78909c;
    content: "Invoice amount";
    display: block;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    line-height: 1;
    text-transform: uppercase;
  }

  .invoice-period {
    align-items: flex-start;
    background: #263238;
    border: 1px solid #263238;
    color: #ffffff;
    flex-wrap: wrap;
    margin-left: 0;
    margin-top: 0;
    padding: 7px 9px;
    max-width: 100%;
    width: fit-content;
  }

  .invoice-action {
    border-radius: 6px;
    float: none !important;
    gap: 8px;
    height: 42px;
    width: 100%;
  }

  .invoice-action::after {
    content: "Open invoice";
    font-size: 0.82rem;
    font-weight: 800;
  }

  .invoice-action.is-close::after {
    content: "Back";
  }

  .finance-approval-card {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
  
